import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  BorderWidthProps,
  LeftProps,
  PositionProps,
  SizeProps,
  SpaceProps,
  TopProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  borderWidth,
  left,
  position,
  size,
  space,
  top,
  width,
  zIndex,
} from 'styled-system';

import type { OutlineProps } from '../theme/system-types';
import type { CursorProps, PointerEventProps } from '../theme/system-utilities';
import { cursor, outline, pointerEvents } from '../theme/system-utilities';
import { Flex } from './Flex';
import type { IconName } from './Icon';
import Icon from './Icon';

interface SlideImageWrapperProps {
  icon?: IconName;
}

const ICON_SIZE = 30;

const Wrapper = styled.button<
  SlideImageWrapperProps &
    SizeProps &
    PositionProps &
    OutlineProps &
    ZIndexProps &
    BackgroundColorProps &
    SpaceProps &
    BorderProps &
    BorderColorProps &
    BorderWidthProps &
    BorderRadiusProps &
    WidthProps
>`
  ${size};
  ${position};
  ${zIndex};
  ${backgroundColor};
  ${space};
  ${border};
  ${borderColor};
  ${borderWidth};
  ${borderRadius};
  ${width};

  &:focus {
    ${outline};
  }
`;

const wrapperStyles: ComponentProps<typeof Wrapper> = {
  position: 'relative',
  outline: 'none',
  zIndex: 1,
  backgroundColor: 'transparent',
  border: 'none',
  padding: 'sp_0',
  margin: 'sp_0',
};

const UnstyledIconOverlay = styled(Flex)<
  PositionProps &
    TopProps &
    LeftProps &
    SizeProps &
    BackgroundColorProps &
    CursorProps &
    PointerEventProps
>`
  ${position};
  ${top};
  ${left};
  ${size};
  ${backgroundColor};
  ${cursor};
  ${pointerEvents};
`;

const IconOverlay = (props: ComponentProps<typeof UnstyledIconOverlay>) => (
  <UnstyledIconOverlay
    position="absolute"
    top={0}
    left={0}
    size="100%"
    alignItems="center"
    justifyContent="center"
    backgroundColor="overlay.default"
    cursor="pointer"
    pointerEvents="none"
    {...props}
  />
);

const UnstyledThumbnailIcon = styled(Icon)<SizeProps>`
  ${size};
`;

const ThumbnailIcon = (props: ComponentProps<typeof UnstyledThumbnailIcon>) => (
  <UnstyledThumbnailIcon size={ICON_SIZE} {...props} />
);

interface ThumbnailImageWrapperProps extends ComponentProps<typeof Wrapper> {
  ref?: React.Ref<HTMLButtonElement>;
}

const ThumbnailImageWrapper = ({ icon, children, ref, ...props }: ThumbnailImageWrapperProps) => (
  <Wrapper ref={ref} {...wrapperStyles} {...props}>
    {children}
    {!!icon && (
      <IconOverlay>
        <ThumbnailIcon dataTestId={`${icon}-icon`} name={icon} />
      </IconOverlay>
    )}
  </Wrapper>
);

ThumbnailImageWrapper.displayName = 'ThumbnailImageWrapper';

export default ThumbnailImageWrapper;
